import { useMemo, useState, useEffect, cloneElement } from 'react';
import classNames from 'classnames';
import { useAddToCart } from 'api/cart/useAddToCart';
import { BuyDialogTypeSelector } from './BuyDialogTypeSelector';
import { BuyDialogSizeSelector } from './BuyDialogSizeSelector';
import { SizeChartDialog } from './SizeChart/SizeChartDialog';
import { useAnalytics } from 'hooks/useAnalytics';
import { useFreeTees } from 'api/cart/useFreeTees';
import BuyDialogHeader from './BuyDialogHeader';
import { BuyDialogPrint } from './BuyDialogPrint';
import { BuyDialogSuccess } from './BuyDialogSuccess';
import { BuyDialogQuantitySelector } from './BuyDialogQuantitySelector';
import { Dialog } from '@headlessui/react';

const productOrderSteps = {
  selectType: 'type',
  selectSize: 'size',
  selectQuantity: 'quantity',
  checkout: 'checkout',
};

export const BuyDialog = ({ product, children, print = false }) => {
  const initialShoppingItem = useMemo(
    () => ({
      type: print ? 'print' : '',
      variations: [],
      size: print ? 'P' : '',
      qty: 1,
      sku: print ? product.types?.at(0).variations?.at(0).sku : '',
    }),
    [print, product]
  );

  const [isOpen, setIsOpen] = useState(false);
  const [shoppingItem, setShoppingItem] = useState(initialShoppingItem);
  const [currentStep, setCurrentStep] = useState(
    print ? productOrderSteps.selectQuantity : productOrderSteps.selectType
  );
  const [freeTeesMode, setFreeTeesMode] = useState(false);
  const [internalProduct, setInternalProduct] = useState();

  const { addToCartAction, buyNowButtonClickAction } = useAnalytics();
  const addToCart = useAddToCart();
  const { getFreeTees } = useFreeTees();

  const openDialog = () => {
    buyNowButtonClickAction(product);
    setIsOpen(true);
  };

  const onTypeSelectHandler = (value) => {
    setShoppingItem({
      ...shoppingItem,
      type: value,
      variations: internalProduct.types.find((type) => type.name === value)
        .variations,
    });
    setCurrentStep(productOrderSteps.selectSize);
  };

  const onSizeSelectChangeHandler = (value) => {
    setShoppingItem({
      ...shoppingItem,
      ...shoppingItem.variations.find((variation) => variation.size === value),
    });
    setCurrentStep(productOrderSteps.selectQuantity);
  };

  const onQuantitySelectChangeHandler = (value) => {
    setShoppingItem({ ...shoppingItem, qty: value });
  };

  const resetDialog = () => {
    setFreeTeesMode(false);
    setInternalProduct(product);
    setShoppingItem(initialShoppingItem);
    setCurrentStep(
      print ? productOrderSteps.selectQuantity : productOrderSteps.selectType
    );
  };

  const closeDialog = () => {
    setIsOpen(false);
    resetDialog();
  };

  const handleAddToCart = async (e) => {
    e.preventDefault();

    if (!shoppingItem.qty || !shoppingItem.sku) return;

    try {
      await addToCart({ sku: shoppingItem.sku, quantity: shoppingItem.qty });

      if (!freeTeesMode) {
        const freeTees = await getFreeTees();

        if (freeTees?.freeDesignProduct && freeTees.eligible > 0) {
          setCurrentStep(productOrderSteps.selectType);
          setFreeTeesMode(true);
          setShoppingItem({
            ...initialShoppingItem,
            eligible: freeTees.eligible,
          });
          setInternalProduct(freeTees.freeDesignProduct);
        } else {
          setCurrentStep(productOrderSteps.checkout);
        }
      } else {
        setCurrentStep(productOrderSteps.checkout);
      }

      addToCartAction({
        productId: internalProduct.id,
        category: shoppingItem.type,
        name: internalProduct.name,
        price: internalProduct.types.find(
          ({ name }) => name === shoppingItem.type
        ).price,
        variant: shoppingItem.size,
        quantity: shoppingItem.qty,
      });
    } catch (error) {}
  };

  useEffect(() => {
    !internalProduct && setInternalProduct(product);
  }, [internalProduct, product]);

  return (
    <>
      {cloneElement(children, { onClick: openDialog })}

      <Dialog
        as="div"
        className="fixed inset-0 overflow-y-auto flex justify-center z-10"
        id={`buy-dialog-header-${product.id}`}
        open={isOpen}
        onClose={closeDialog}
      >
        <div className="absolute inset-0 bg-black/90" aria-hidden="true" />

        <Dialog.Panel
          className={classNames(
            'border-[10px] border-qw-blue-500 inline-block max-w-[60rem] w-full h-fit mx-auto mt-0 md:mt-[20vh] p-0 font-sans z-10',
            {
              'border-qw-yellow-200': freeTeesMode,
            }
          )}
        >
          <Dialog.Title as="div">
            <BuyDialogHeader isFreeTee={freeTeesMode} onClose={closeDialog} />
          </Dialog.Title>

          <div className="py-3 md:py-5 border border-black bg-white">
            <form onSubmit={handleAddToCart}>
              {print ? (
                <BuyDialogPrint product={internalProduct} />
              ) : (
                <BuyDialogTypeSelector
                  types={internalProduct?.types}
                  value={shoppingItem.type}
                  onChange={onTypeSelectHandler}
                />
              )}

              {currentStep !== productOrderSteps.selectType && (
                <hr className="h-[0.1875rem] md:h-[0.31rem] border-none bg-black w-[90%] my-2.5 md:my-4 mx-auto" />
              )}

              {(currentStep === productOrderSteps.selectType ||
                currentStep === productOrderSteps.selectSize) && (
                <SizeChartDialog type={shoppingItem.type} />
              )}

              {currentStep === productOrderSteps.selectSize && (
                <BuyDialogSizeSelector
                  variations={shoppingItem.variations}
                  value={shoppingItem.size}
                  onChange={onSizeSelectChangeHandler}
                />
              )}

              {currentStep === productOrderSteps.selectQuantity && (
                <BuyDialogQuantitySelector
                  shoppingItem={shoppingItem}
                  stock={shoppingItem.eligible || shoppingItem.stock}
                  onChange={onQuantitySelectChangeHandler}
                />
              )}

              {currentStep === productOrderSteps.checkout && (
                <BuyDialogSuccess
                  shoppingItem={shoppingItem}
                  product={internalProduct}
                  isFreeTee={freeTeesMode}
                  onClose={closeDialog}
                />
              )}
            </form>
          </div>
        </Dialog.Panel>
      </Dialog>
    </>
  );
};
